import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { platformApiFetch } from './platformApi';
import { usePlatformAuth } from '../context/PlatformAuthContext';

function statusColor(s) {
  if (s === 'paid') return 'success';
  if (s === 'overdue' || s === 'void') return 'error';
  if (s === 'draft') return 'default';
  return 'info';
}

export default function PlatformBillingInvoiceDetailPage() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const { user, ready } = usePlatformAuth();
  const isSuper = user?.role === 'super_admin';

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [delOpen, setDelOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/billing/invoices/${invoiceId}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Not found');
      setInvoice(json.invoice);
    } catch (e) {
      setError(e.message);
      setInvoice(null);
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    if (ready) load();
  }, [load, ready]);

  const doDelete = async () => {
    setDeleting(true);
    try {
      const res = await platformApiFetch(`/api/platform/billing/invoices/${invoiceId}`, { method: 'DELETE' });
      if (res.status === 204 || res.ok) {
        navigate('/platform/billing/invoices');
        return;
      }
      const json = await res.json().catch(() => ({}));
      throw new Error(json.error || 'Delete failed');
    } catch (e) {
      setError(e.message);
    } finally {
      setDeleting(false);
      setDelOpen(false);
    }
  };

  if (!ready || loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !invoice) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!invoice) return null;

  const row = (label, value) => (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1">{value ?? '—'}</Typography>
    </Grid>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5">Invoice {invoice.invoice_reference}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {invoice.tenant_name} (#{invoice.tenant_id})
          </Typography>
          <Chip size="small" label={invoice.invoice_status} color={statusColor(invoice.invoice_status)} sx={{ mt: 1 }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" component={Link} to="/platform/billing/invoices">
            Back to list
          </Button>
          {isSuper && (
            <>
              <Button variant="contained" component={Link} to={`/platform/billing/invoices/${invoiceId}/edit`}>
                Edit
              </Button>
              <Button color="error" variant="outlined" onClick={() => setDelOpen(true)}>
                Delete
              </Button>
            </>
          )}
        </Box>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Paper sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {row('Amount', invoice.amount != null ? `${invoice.amount.toFixed(2)} ${invoice.currency}` : '—')}
          {row('Billing period', invoice.billing_period)}
          {row('Issued', invoice.issued_at ? String(invoice.issued_at) : '—')}
          {row('Due', invoice.due_at ? String(invoice.due_at) : '—')}
          {row('Paid', invoice.paid_at ? String(invoice.paid_at) : '—')}
          {row('Created', invoice.created_at ? String(invoice.created_at) : '—')}
          {row('Updated', invoice.updated_at ? String(invoice.updated_at) : '—')}
        </Grid>
      </Paper>

      <Dialog open={delOpen} onClose={() => !deleting && setDelOpen(false)}>
        <DialogTitle>Delete invoice?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes invoice {invoice.invoice_reference} for tenant #{invoice.tenant_id}. Platform audit will record the
            deletion.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDelOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" onClick={doDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
